import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [showCompanyFields, setShowCompanyFields] = useState(false);
  const { register, handleSubmit, control, watch, formState: { errors } } = useForm();
  const numberOfEmployees = watch('numberOfEmployees');

  const onSubmit = async (data: any) => {
    try {
      const companyData = showCompanyFields ? {
        companyName: data.companyName,
        companyLocation: data.companyLocation,
        companyType: data.companyType,
        numberOfEmployees: data.numberOfEmployees || 1,
      } : undefined;
      
      await registerUser(data.email, data.password, data.name, companyData);
      navigate('/');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Barefoot SBA
          </Typography>
          <Typography component="h2" variant="h6" align="center" gutterBottom>
            Sign Up
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Name"
              autoFocus
              {...register('name', { required: 'Name is required' })}
              error={!!errors.name}
              helperText={errors.name?.message as string}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              autoComplete="email"
              {...register('email', { required: 'Email is required' })}
              error={!!errors.email}
              helperText={errors.email?.message as string}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Password must be at least 8 characters' } })}
              error={!!errors.password}
              helperText={errors.password?.message as string}
            />
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={() => setShowCompanyFields(!showCompanyFields)}
              >
                {showCompanyFields ? 'Hide' : 'Add'} Company Information (Optional)
              </Button>
            </Box>

            {showCompanyFields && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      {...register('companyName')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      {...register('companyLocation')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="companyType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Company Type</InputLabel>
                          <Select {...field} label="Company Type">
                            <MenuItem value="Sole Trader">Sole Trader</MenuItem>
                            <MenuItem value="Partnership">Partnership</MenuItem>
                            <MenuItem value="Limited Company">Limited Company</MenuItem>
                            <MenuItem value="LLC">LLC</MenuItem>
                            <MenuItem value="Corporation">Corporation</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="numberOfEmployees"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Number of Employees"
                          inputProps={{ min: 1 }}
                          helperText={numberOfEmployees === 1 ? 'Sole trader - Full admin access' : 'Multi-user setup'}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign Up
            </Button>
            <Box textAlign="center">
              <Link href="/login" variant="body2">
                Already have an account? Sign In
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;

